/**
 * Secure Image Component
 * Uses authenticated backend proxy to serve images securely
 * Replaces direct S3 presigned URL access for better security
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AuthCore } from '../../core/AuthCore';

interface SecureImageProps {
  assetId: string;
  alt?: string;
  className?: string;
  version?: 'thumbnail' | 'large';
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: (error: string) => void;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
}

interface ImageCache {
  blob: string;
  timestamp: number;
}

// Cache for blob URLs with 30-minute expiry
const imageCache = new Map<string, ImageCache>();
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// 🚀 PERFORMANCE: Memoize SecureImage to prevent unnecessary re-renders
export const SecureImage = React.memo(function SecureImage({
  assetId,
  alt = '',
  className = '',
  version = 'thumbnail',
  fallbackSrc,
  onLoad,
  onError,
  style,
  loading = 'lazy'
}: SecureImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const mountedRef = useRef(true);
  
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
  const cacheKey = `${assetId}_${version}`;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const fetchImage = useCallback(async (retryCount = 0) => {
    if (!assetId) return;

    setIsLoading(true);
    setHasError(false);

    // Removed console.log for performance in production

    try {
      // Check cache first
      const cached = imageCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        // Removed console.log for performance in production
        setImageUrl(cached.blob);
        setIsLoading(false);
        onLoad?.();
        return;
      }

      // Get authentication token
      const token = await AuthCore.getInstance().getAccessToken();
      
      if (!token) {
        console.error('❌ SecureImage: No authentication token available', { assetId, version });
        setHasError(true);
        setIsLoading(false);
        onError?.('No authentication token available');
        return;
      }

      const imageUrl = `${baseUrl}/api/image/${assetId}/${version}`;

      // Fetch image with authentication
      const response = await fetch(imageUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Convert to blob and create URL
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Cache the blob URL
      imageCache.set(cacheKey, {
        blob: blobUrl,
        timestamp: Date.now()
      });

      if (mountedRef.current) {
        // Removed console.log for performance in production
        setImageUrl(blobUrl);
        setIsLoading(false);
        onLoad?.();
      } else {
        URL.revokeObjectURL(blobUrl);
      }

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      
      // Retry logic: up to 3 attempts with exponential backoff
      if (retryCount < 3 && mountedRef.current) {
        const delay = 1000 * Math.pow(2, retryCount); // 1s, 2s, 4s
        console.warn(`⚠️ SecureImage: Retry ${retryCount + 1}/3 after ${delay}ms`, { 
          assetId, 
          version, 
          error: errorMsg 
        });
        
        await sleep(delay);
        
        // Retry if still mounted
        if (mountedRef.current) {
          return fetchImage(retryCount + 1);
        }
      }
      
      // Final error after all retries
      console.error('❌ SecureImage: Failed after all retries', {
        assetId,
        version,
        retryCount,
        error: errorMsg
      });
      
      if (mountedRef.current) {
        setHasError(true);
        setIsLoading(false);
        onError?.(errorMsg);
      }
    }
  }, [assetId, version, baseUrl, cacheKey, onLoad, onError]);

  useEffect(() => {
    fetchImage();

    // Cleanup blob URL on unmount
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [fetchImage]);

  // Clean up expired cache entries periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      for (const [key, value] of imageCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
          URL.revokeObjectURL(value.blob);
          imageCache.delete(key);
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.();
  };

  const handleImageError = () => {
    const errorMsg = 'Failed to load image';
    setHasError(true);
    setIsLoading(false);
    onError?.(errorMsg);
  };

  // Loading state
  if (isLoading) {
    return (
      <div 
        className={`secure-image-loading ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
          minHeight: '100px',
          ...style
        }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div 
        className={`secure-image-error ${className}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          padding: '1rem',
          minHeight: '100px',
          ...style
        }}
      >
        {fallbackSrc ? (
          <img 
            src={fallbackSrc}
            alt={alt}
            className="max-w-full max-h-full object-contain"
            onError={() => {}}
          />
        ) : (
          <>
            <svg className="w-8 h-8 mb-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-center">
              {version === 'thumbnail' ? 'Thumbnail not available' : 'Image not available'}
            </span>
          </>
        )}
      </div>
    );
  }

  // Success state
  return (
    <img
      ref={imgRef}
      src={imageUrl || undefined}
      alt={alt}
      className={`secure-image ${className}`}
      style={style}
      loading={loading}
      onLoad={handleImageLoad}
      onError={handleImageError}
      // Prevent right-click save (basic protection)
      onContextMenu={(e) => {
        e.preventDefault();
        return false;
      }}
      // Prevent dragging
      onDragStart={(e) => {
        e.preventDefault();
        return false;
      }}
      // Additional security attributes
      crossOrigin="anonymous"
    />
  );
});

// 🚀 PERFORMANCE: Add displayName for React.memo
SecureImage.displayName = 'SecureImage';

/**
 * Secure Image Gallery Component
 * Displays multiple images with preloading for better performance
 */
export interface SecureImageGalleryProps {
  assetIds: string[];
  version?: 'thumbnail' | 'large';
  className?: string;
  imageClassName?: string;
  onImageClick?: (assetId: string) => void;
  cols?: number;
}

export function SecureImageGallery({
  assetIds,
  version = 'thumbnail',
  className = '',
  imageClassName = '',
  onImageClick,
  cols = 3
}: SecureImageGalleryProps) {
  // Note: Preloading is now handled by the SecureImage component itself
  // The blob cache will automatically handle efficient loading

  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6'
  }[cols] || 'grid-cols-3';

  return (
    <div className={`secure-image-gallery grid gap-4 ${gridCols} ${className}`}>
      {assetIds.map((assetId) => (
        <div 
          key={assetId}
          className="relative cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => onImageClick?.(assetId)}
        >
          <SecureImage
            assetId={assetId}
            version={version}
            className={`w-full h-48 object-cover rounded-lg ${imageClassName}`}
            alt={`Asset ${assetId}`}
            loading="lazy"
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Hook for secure image management
 */
export function useSecureImageDownload() {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

  const downloadImage = async (assetId: string, filename?: string) => {
    try {
      const token = await AuthCore.getInstance().getAccessToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Fetch the large version of the image
      const response = await fetch(`${baseUrl}/api/image/${assetId}/large`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Convert to blob and create download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `image-${assetId}`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error downloading image:', error);
      return false;
    }
  };

  const clearImageCache = () => {
    for (const [key, value] of imageCache.entries()) {
      URL.revokeObjectURL(value.blob);
      imageCache.delete(key);
    }
  };

  return { downloadImage, clearImageCache };
}
